import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import LangToggle from "@/components/LangToggle";

export default function Onboarding() {
  const { t, lang, setLang } = useLang();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    tone: lang === "en" ? "you" : "tu",
    language: lang,
    family_context: "",
    children: "",
    perimeter_notes: "",
    weekly_summary_day: "sunday",
    perimeter_level: "verbose",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post("/onboarding", form);
      await refreshUser();
      setLang(form.language);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const next = () => setStep((s) => Math.min(5, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="max-w-5xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between">
        <span className="font-display text-2xl">Mordomo</span>
        <div className="flex items-center gap-8">
          <p className="label-cap" data-testid="onboarding-step-indicator">
            {t("onboarding.stepOf", { n: step })}
          </p>
          <LangToggle />
        </div>
      </header>

      <hr className="editorial-rule" />

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="mb-12">
          <p className="label-cap">— Onboarding</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4">
            {t("onboarding.title")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] text-lg mt-3">
            {t("onboarding.sub")}
          </p>
        </div>

        <div className="card-flat" data-testid={`onboarding-step-${step}`}>
          {step === 1 && (
            <Step
              title={t("onboarding.q1Title")}
              sub={t("onboarding.q1Sub")}
            >
              <div className="flex flex-col gap-3 mt-2">
                <Radio
                  active={form.tone === "tu"}
                  label={t("onboarding.toneTu")}
                  onClick={() => set("tone", "tu")}
                  testId="onb-tone-tu"
                />
                <Radio
                  active={form.tone === "voce"}
                  label={t("onboarding.toneVoce")}
                  onClick={() => set("tone", "voce")}
                  testId="onb-tone-voce"
                />
                <Radio
                  active={form.tone === "you"}
                  label={t("onboarding.toneYou")}
                  onClick={() => set("tone", "you")}
                  testId="onb-tone-you"
                />
              </div>
              <div className="mt-8">
                <p className="label-cap mb-2">{t("auth.language")}</p>
                <div className="flex gap-3">
                  <ChipButton active={form.language === "pt"} onClick={() => set("language", "pt")} testId="onb-lang-pt">
                    Português
                  </ChipButton>
                  <ChipButton active={form.language === "en"} onClick={() => set("language", "en")} testId="onb-lang-en">
                    English
                  </ChipButton>
                </div>
              </div>
            </Step>
          )}

          {step === 2 && (
            <Step title={t("onboarding.q2Title")} sub={t("onboarding.q2Sub")}>
              <div className="space-y-6 mt-2">
                <label className="block">
                  <span className="label-cap">{t("onboarding.familyLabel")}</span>
                  <textarea
                    rows={3}
                    className="line-input resize-none"
                    placeholder={t("onboarding.familyPlaceholder")}
                    value={form.family_context}
                    onChange={(e) => set("family_context", e.target.value)}
                    data-testid="onb-family-input"
                  />
                </label>
                <label className="block">
                  <span className="label-cap">{t("onboarding.childrenLabel")}</span>
                  <textarea
                    rows={2}
                    className="line-input resize-none"
                    placeholder={t("onboarding.childrenPlaceholder")}
                    value={form.children}
                    onChange={(e) => set("children", e.target.value)}
                    data-testid="onb-children-input"
                  />
                </label>
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step title={t("onboarding.q3Title")} sub={t("onboarding.q3Sub")}>
              <label className="block mt-2">
                <textarea
                  rows={5}
                  className="line-input resize-none"
                  placeholder={t("onboarding.perimeterPlaceholder")}
                  value={form.perimeter_notes}
                  onChange={(e) => set("perimeter_notes", e.target.value)}
                  data-testid="onb-perimeter-input"
                />
              </label>
            </Step>
          )}

          {step === 4 && (
            <Step title={t("onboarding.q4Title")} sub={t("onboarding.q4Sub")}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {[
                  { v: "sunday", l: t("onboarding.sunday") },
                  { v: "monday", l: t("onboarding.monday") },
                  { v: "friday", l: t("onboarding.friday") },
                  { v: "saturday", l: t("onboarding.saturday") },
                ].map((d) => (
                  <ChipButton
                    key={d.v}
                    active={form.weekly_summary_day === d.v}
                    onClick={() => set("weekly_summary_day", d.v)}
                    testId={`onb-day-${d.v}`}
                  >
                    {d.l}
                  </ChipButton>
                ))}
              </div>
            </Step>
          )}

          {step === 5 && (
            <Step title={t("onboarding.q5Title")} sub={t("onboarding.q5Sub")}>
              <div className="flex flex-col gap-3 mt-2">
                <Radio
                  active={form.perimeter_level === "verbose"}
                  label={t("onboarding.verbose")}
                  onClick={() => set("perimeter_level", "verbose")}
                  testId="onb-mode-verbose"
                />
                <Radio
                  active={form.perimeter_level === "silent"}
                  label={t("onboarding.silent")}
                  onClick={() => set("perimeter_level", "silent")}
                  testId="onb-mode-silent"
                />
              </div>
              <p className="font-display-italic text-[var(--ink-soft)] mt-12 text-lg">
                {t("onboarding.welcomeNote")}
              </p>
            </Step>
          )}

          {error && <p className="text-[var(--error)] text-sm mt-6" data-testid="onb-error">{error}</p>}

          <div className="mt-12 flex items-center justify-between gap-6 pt-8 border-t border-[var(--border)]">
            <button
              onClick={back}
              disabled={step === 1}
              className="btn-ghost disabled:opacity-0"
              data-testid="onb-back-btn"
            >
              ← {t("onboarding.back")}
            </button>
            {step < 5 ? (
              <button onClick={next} className="btn-primary" data-testid="onb-next-btn">
                {t("onboarding.next")} →
              </button>
            ) : (
              <button onClick={onSubmit} disabled={submitting} className="btn-primary" data-testid="onb-finish-btn">
                {submitting ? t("onboarding.finishing") : t("onboarding.finish")}
              </button>
            )}
          </div>
        </div>

        {/* Progress dots */}
        <div className="mt-10 flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className={`h-px transition-all duration-500 ${
                i <= step ? "bg-[var(--ink)] w-12" : "bg-[var(--border)] w-6"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Step({ title, sub, children }) {
  return (
    <div>
      <h2 className="font-display text-3xl md:text-4xl font-light leading-tight tracking-tight">
        {title}
      </h2>
      <p className="font-display-italic text-[var(--ink-soft)] mt-3 text-base">{sub}</p>
      <div className="mt-10">{children}</div>
    </div>
  );
}

function Radio({ active, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-4 p-5 border transition-all text-left ${
        active
          ? "border-[var(--ink)] bg-[var(--bg)]"
          : "border-[var(--border)] hover:border-[var(--ink)]"
      }`}
    >
      <span
        className={`w-3 h-3 rounded-full border ${
          active ? "border-[var(--ink)] bg-[var(--ink)]" : "border-[var(--ink-soft)]"
        }`}
      />
      <span className={active ? "text-[var(--ink)]" : "text-[var(--ink-soft)]"}>{label}</span>
    </button>
  );
}

function ChipButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-[12px] tracking-monocle uppercase border transition-colors duration-300 ${
        active
          ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
          : "bg-transparent text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
      }`}
    >
      {children}
    </button>
  );
}
